import Immutable from "immutable";
import React from "react";
import * as State from "src/scenes/orgAdmin/formTemplates/types/settingsState";
import {
  Change,
  FormTemplateId,
  FormTemplateIdType,
  hasChanges,
} from "../types/common";
import * as Draft from "../types/settingsDraft";
import { FormTemplateSettingsType } from "./schemas";

export type SettingsInitialValues = {
  settings: FormTemplateSettingsType;
};

export type Action =
  | {
      type: "UpdateSettings";
      settings: Draft.SettingsDraft;
      formTemplateId: uuid;
    }
  | {
      type: "Refresh";
      initialValues: SettingsInitialValues;
      formTemplateId: uuid;
    }
  | { type: "Reset" };

export function reducer(state: State.State, action: Action): State.State {
  switch (action.type) {
    case "Reset":
      return reset(state);

    case "UpdateSettings":
      return updateSettings(state, action.settings, action.formTemplateId);

    case "Refresh":
      return refresh(action.initialValues, action.formTemplateId);

    default:
      const _exhaustiveCheck: never = action;
      return _exhaustiveCheck;
  }
}

function reset(state: State.State): State.State {
  const settings = state.get("settings").map(clearChange);
  return state.set("settings", settings);
}

function clearChange<T, U>(change: Change<T, U>): Change<T, U> {
  return { ...change, draft: undefined };
}

function updateSettings(
  state: State.State,
  draft: Draft.SettingsDraft,
  formTemplateId: uuid
): State.State {
  const originalSettingsState = state
    .get("settings")
    .get(FormTemplateId(formTemplateId));
  if (!originalSettingsState) {
    return state;
  }

  const original = originalSettingsState.original;
  const hasChangesResult = hasChanges(original, draft);

  return state.update("settings", (settings) =>
    settings.set(FormTemplateId(formTemplateId), {
      ...originalSettingsState,
      draft: hasChangesResult ? draft : undefined,
    })
  );
}

function toState(
  initialValues: SettingsInitialValues,
  formTemplateId: uuid
): State.State {
  const settings: [FormTemplateIdType, State.SettingsChange][] = [
    [
      FormTemplateId(formTemplateId),
      { original: initialValues.settings, draft: undefined },
    ],
  ];

  return State.NewState({
    settings: Immutable.Map(settings),
  });
}

function refresh(
  initialValues: SettingsInitialValues,
  formTemplateId: uuid
): State.State {
  return reset(toState(initialValues, formTemplateId));
}

export function useFormTemplateSettingsProcessor(
  initialValues: SettingsInitialValues,
  formTemplateId: uuid
) {
  const initialState = toState(initialValues, formTemplateId);
  const [state, dispatch] = React.useReducer(reducer, initialState);

  return { state, dispatch };
}
